import React from "react";
import { AlertTriangle, TrendingUp, Users, Package, ArrowUpRight, Cloud } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from "recharts";

const fmt = (n) => "$" + Number(n || 0).toLocaleString();
const money = (n) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n}`;

/**
 * Oracle Cloud ERP executive cockpit — Financials + Supply Chain + HCM.
 *
 * Presentation-grade mock. When a tenant connects Oracle Fusion via OAuth 2.0
 * client credentials on the Connections tab, /api/platinum/oracle-erp swaps in
 * live REST responses.
 *
 * Sub-sections mirror D365 for cognitive continuity but showcase Oracle's
 * particular strengths (Financials Cloud, SCM Cloud, HCM Cloud).
 */
const MOCK = {
  source: "Demonstration data · connect your Oracle Fusion tenant on Connections to swap",
  financials: {
    revenueMtd: 12_450_000,
    revenueQtd: 34_120_000,
    ebitdaPct: 18.4,
    cashOnHand: 78_600_000,
    dso: 41,
    dpo: 52,
    closedDays: 3.5,
    series: [
      { month: "Mar", revenue: 10_820_000, ebitda: 1_990_000, gross: 4_320_000 },
      { month: "Apr", revenue: 11_040_000, ebitda: 2_080_000, gross: 4_410_000 },
      { month: "May", revenue: 11_310_000, ebitda: 2_130_000, gross: 4_510_000 },
      { month: "Jun", revenue: 11_620_000, ebitda: 2_180_000, gross: 4_630_000 },
      { month: "Jul", revenue: 11_890_000, ebitda: 2_240_000, gross: 4_740_000 },
      { month: "Aug", revenue: 12_120_000, ebitda: 2_290_000, gross: 4_820_000 },
      { month: "Sep", revenue: 12_240_000, ebitda: 2_320_000, gross: 4_870_000 },
      { month: "Oct", revenue: 12_100_000, ebitda: 2_280_000, gross: 4_830_000 },
      { month: "Nov", revenue: 12_360_000, ebitda: 2_340_000, gross: 4_910_000 },
      { month: "Dec", revenue: 12_720_000, ebitda: 2_410_000, gross: 5_050_000 },
      { month: "Jan", revenue: 12_310_000, ebitda: 2_350_000, gross: 4_890_000 },
      { month: "Feb", revenue: 12_450_000, ebitda: 2_370_000, gross: 4_940_000 },
    ],
  },
  supplyChain: {
    otdPct: 96.4,
    inventoryTurns: 8.6,
    procureCycleDays: 11.2,
    supplierCount: 342,
    byCategory: [
      { name: "Direct materials", value: 62 },
      { name: "Logistics",         value: 18 },
      { name: "Indirect",          value: 12 },
      { name: "Services",          value: 8  },
    ],
  },
  hcm: {
    headcount: 2840,
    attritionPct: 8.6,
    openPositions: 47,
    engagementScore: 78,
    byFunction: [
      { name: "Engineering",  value: 940 },
      { name: "Sales",         value: 610 },
      { name: "Operations",    value: 720 },
      { name: "Support",       value: 320 },
      { name: "Finance/G&A",   value: 250 },
    ],
  },
};

export default function OracleCockpit() {
  const d = MOCK;
  const f = d.financials, sc = d.supplyChain, h = d.hcm;

  return (
    <div className="space-y-6" data-testid="oracle-cockpit">
      <Header source={d.source} />

      {/* Financials Cloud */}
      <Section title="Oracle Financials Cloud" eyebrow="Module 01">
        <div className="grid md:grid-cols-4 gap-3 mb-4">
          <Tile label="Revenue · MTD"  value={money(f.revenueMtd)} tone="red"/>
          <Tile label="Revenue · QTD"  value={money(f.revenueQtd)} tone="red"/>
          <Tile label="EBITDA %"       value={`${f.ebitdaPct}%`}   tone="green"/>
          <Tile label="Cash on hand"   value={money(f.cashOnHand)} tone="neutral" sub={`Close ${f.closedDays}d · DSO ${f.dso} · DPO ${f.dpo}`}/>
        </div>
        <div className="card p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Revenue · trailing 12 months</div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={f.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
                <XAxis dataKey="month" tick={{ fontSize: 10 }}/>
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${v / 1_000_000}M`}/>
                <Tooltip formatter={v => fmt(v)} contentStyle={{ fontSize: 12 }}/>
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle"/>
                <Line dataKey="revenue" stroke="#DC2626" strokeWidth={2} name="Revenue" dot={false}/>
                <Line dataKey="ebitda"  stroke="#EA580C" strokeWidth={2} name="EBITDA"  dot={false}/>
                <Line dataKey="gross"   stroke="#F59E0B" strokeWidth={2} name="Gross"   dot={false}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Section>

      {/* SCM Cloud */}
      <Section title="Oracle SCM Cloud" eyebrow="Module 02">
        <div className="grid md:grid-cols-4 gap-3 mb-4">
          <Tile label="On-time delivery" value={`${sc.otdPct}%`}          tone="green"/>
          <Tile label="Inventory turns"  value={sc.inventoryTurns}         tone="blue"/>
          <Tile label="Procure cycle"    value={`${sc.procureCycleDays}d`} tone="neutral"/>
          <Tile label="Suppliers"        value={sc.supplierCount}          tone="neutral"/>
        </div>
        <div className="card p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Spend by category · %</div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={sc.byCategory} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`}/>
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120}/>
                <Tooltip formatter={v => `${v}%`} contentStyle={{ fontSize: 12 }}/>
                <Bar dataKey="value" fill="#DC2626" radius={[0, 3, 3, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Section>

      {/* HCM Cloud */}
      <Section title="Oracle HCM Cloud" eyebrow="Module 03">
        <div className="grid md:grid-cols-4 gap-3 mb-4">
          <Tile label="Headcount"        value={h.headcount.toLocaleString()} tone="blue"/>
          <Tile label="Attrition · TTM"  value={`${h.attritionPct}%`}          tone={h.attritionPct > 12 ? "red" : "green"}/>
          <Tile label="Open positions"   value={h.openPositions}                tone="neutral"/>
          <Tile label="Engagement score" value={`${h.engagementScore}/100`}    tone={h.engagementScore >= 75 ? "green" : "neutral"}/>
        </div>
        <div className="card p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Headcount by function</div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={h.byFunction}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
                <XAxis dataKey="name" tick={{ fontSize: 10 }}/>
                <YAxis tick={{ fontSize: 10 }}/>
                <Tooltip contentStyle={{ fontSize: 12 }}/>
                <Bar dataKey="value" fill="#7A1F2B" radius={[3, 3, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Section>
    </div>
  );
}

function Header({ source }) {
  return (
    <div className="p-4 rounded-sm border flex items-start gap-3"
         style={{ background: "linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)", borderColor: "#FCA5A5" }}>
      <span className="w-9 h-9 rounded-sm grid place-items-center shrink-0 text-white"
            style={{ background: "linear-gradient(135deg, #7A1F2B 0%, #DC2626 100%)" }}>
        <Cloud size={16}/>
      </span>
      <div className="flex-1">
        <div className="font-display text-base font-bold text-[#0F1723]">Oracle Cloud ERP Executive Cockpit</div>
        <div className="text-xs text-zinc-600 mt-0.5">{source}</div>
      </div>
      <a href="#connections" className="font-mono text-[10px] uppercase tracking-widest text-red-700 inline-flex items-center gap-1 hover:underline" data-testid="oracle-connect-link">
        Connect tenant <ArrowUpRight size={11}/>
      </a>
    </div>
  );
}

function Section({ title, eyebrow, children }) {
  return (
    <div>
      <div className="flex items-baseline gap-3 mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-700">{eyebrow}</span>
        <h3 className="font-display text-lg font-extrabold text-[#0F1723]">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Tile({ label, value, sub, tone = "neutral" }) {
  const bg = tone === "red"    ? "#FEF2F2"
           : tone === "green"  ? "#F0FDF4"
           : tone === "blue"   ? "#F0F9FF"
           :                     "#FAFAF9";
  const brd = tone === "red"    ? "#FCA5A5"
            : tone === "green"  ? "#86EFAC"
            : tone === "blue"   ? "#7DD3FC"
            :                     "#E5E5E5";
  const fg = tone === "red"    ? "#B91C1C"
           : tone === "green"  ? "#166534"
           : tone === "blue"   ? "#075985"
           :                     "#1F2937";
  return (
    <div className="p-3 rounded-sm border" style={{ background: bg, borderColor: brd }}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display text-xl font-extrabold mt-0.5" style={{ color: fg }}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}
